import React from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import ConfirmActionModal from "./ConfirmActionModal";

interface DeleteLicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => void;
  // screens can be a flat array or grouped { licenseScreens: Screen[]; addOnScreens: Screen[] }
  screens: any;
  license: any;
}

const DeleteLicenseModal: React.FC<DeleteLicenseModalProps> = ({
  isOpen,
  onClose,
  onDelete,
  screens = [],
  license,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [showConfirm, setShowConfirm] = React.useState(false);
  const isGrouped =
    screens &&
    !Array.isArray(screens) &&
    (screens.licenseScreens || screens.addOnScreens);
  const normalizeVariant = (v: any) => String(v || "").toUpperCase();
  const isAddon = (s: any) =>
    normalizeVariant(
      s?.variant || s?.plan?.variant || s?.planVariant || s?.type
    ) === "ADDON_SCREEN";
  const licenseScreens: any[] = isGrouped
    ? screens.licenseScreens || []
    : Array.isArray(screens)
      ? screens.filter((s: any) => !isAddon(s))
      : [];
  const addOnScreens: any[] = isGrouped
    ? screens.addOnScreens || []
    : Array.isArray(screens)
      ? screens.filter((s: any) => isAddon(s))
      : [];

  if (!isOpen) return null;
  // Formatting helpers
  const formatDate = (dateString: string) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString("en-GB"); // DD/MM/YYYY
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-2xl rounded-lg shadow-2xl border ${isDark
          ? "bg-[#1B1B1B] border-[#3A3A3A] text-gray-100"
          : "bg-white border-gray-200 text-gray-900"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? "border-[#3A3A3A]" : "border-gray-200"
            }`}
        >
          <h3
            className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"
              }`}
          >
            License Details
          </h3>
          <button
            onClick={onClose}
            className={`w-6 h-6 flex items-center justify-center rounded-[7.529px] transition-opacity ${isDark
              ? "bg-[#2E2E2E] shadow-[0_-0.941px_0.941px_0_rgba(150,161,172,0.12)_inset] hover:opacity-80"
              : "bg-gray-100 hover:bg-gray-200"
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M12.4235 2.63525L2.63525 12.4235M2.63525 2.63525L12.4235 12.4235"
                stroke={isDark ? "#F3F3F6" : "#4B5563"}
                strokeWidth="1.12942"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 text-[11px] space-y-5 ">
          {/* Top summary grid - 4 columns layout for precise alignment */}
          <div
            className={`grid grid-cols-[auto_1fr_auto_1fr] gap-x-12 gap-y-4 rounded-md px-4 py-4 text-[11px] ${isDark ? "bg-[#252525]" : "bg-gray-50"
              }`}
          >
            {/* Row 1 */}
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              License ID
            </div>
            <div className="text-[11px] font-bold text-[#EDF5C0]">
              {license?.code || license?._id || "--"}
            </div>
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              License Type
            </div>
            <div className="text-[11px] font-bold text-[#EDF5C0]">
              {license?.type || "--"}
            </div>

            {/* Row 2 */}
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Assigned to
            </div>
            <div className={`text-[11px] font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {license.assignedGym?.name || "--"}
            </div>
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Location
            </div>
            <div className={`text-[11px] font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {license.assignedGym?.location || "--"}
            </div>

            {/* Row 3 */}
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Billed Date
            </div>
            <div className={`text-[11px] font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {formatDate(license?.startDate)}
            </div>
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              End Date
            </div>
            <div className={`text-[11px] font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {formatDate(license?.endDate)}
            </div>

            {/* Row 4 */}
            <div className={`text-[10px] ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Total
            </div>
            <div className={`text-[11px] font-bold ${isDark ? "text-gray-200" : "text-gray-100"}`}>
              {license?.totalAmount ?? 0}
            </div>
            <div></div>
            <div></div>
          </div>

          {/* License Screens Details */}
          <div className="space-y-2">
            <div
              className={`text-[12px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"
                }`}
            >
              License Screens Details
            </div>
            <div
              className={`rounded-md border overflow-hidden text-[11px] ${isDark
                ? "border-[#3A3A3A] bg-[#111111]"
                : "border-gray-200 bg-gray-50"
                }`}
            >
              {/* Sticky Header */}
              <div
                className={`sticky top-0 flex justify-between px-4 py-2 border-b ${isDark
                  ? "border-[#3A3A3A] text-gray-400 bg-[#323232]"
                  : "border-gray-200 text-gray-500 bg-gray-100"
                  }`}
              >
                <span>ID</span>
                <span>Status</span>
              </div>

              {/* Scrollable Content */}
              <div
                className={`max-h-[120px] overflow-y-auto ${isDark ? "bg-[#1B1B1B]" : "bg-white"
                  }`}
              >
                {licenseScreens.length === 0 ? (
                  <div className="px-4 py-2 text-gray-400">
                    No screens found.
                  </div>
                ) : (
                  licenseScreens.map((screen: any, index: number) => (
                    <div
                      key={screen._id || index}
                      className={`px-4 py-2 flex justify-between ${index !== 0 ? "border-t" : ""
                        } ${isDark ? "border-[#3A3A3A]" : "border-gray-200"}`}
                    >
                      <span className="text-gray-300">
                        {screen.screenId || screen.code || screen._id}
                      </span>
                      <span className="text-[#C6D92C] font-medium">
                        {screen.status || "Active"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Addon Screens Details */}
          <div className="space-y-2">
            <div
              className={`text-[12px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"
                }`}
            >
              Addon Screens Details
            </div>
            <div
              className={`rounded-md border overflow-hidden text-[11px] ${isDark
                ? "border-[#3A3A3A] bg-[#111111]"
                : "border-gray-200 bg-gray-50"
                }`}
            >
              {/* Sticky Header */}
              <div
                className={`sticky top-0 flex justify-between px-4 py-2 border-b ${isDark
                  ? "border-[#3A3A3A] text-gray-400 bg-[#323232]"
                  : "border-gray-200 text-gray-500 bg-gray-100"
                  }`}
              >
                <span>ID</span>
                <span>Status</span>
              </div>

              {/* Scrollable Content */}
              <div
                className={`max-h-[120px] overflow-y-auto ${isDark ? "bg-[#1B1B1B]" : "bg-white"
                  }`}
              >
                {addOnScreens.length === 0 ? (
                  <div className="px-4 py-2 text-gray-400">
                    No add-on screens found.
                  </div>
                ) : (
                  addOnScreens.map((screen: any, index: number) => (
                    <div
                      key={screen._id || index}
                      className={`px-4 py-2 flex justify-between ${index !== 0 ? "border-t" : ""
                        } ${isDark ? "border-[#3A3A3A]" : "border-gray-200"}`}
                    >
                      <span className="text-gray-300">
                        {screen.screenId || screen.code || screen._id}
                      </span>
                      <span className="text-[#C6D92C] font-medium">
                        {screen.status || "Active"}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className={`px-5 py-4 border-t flex justify-center gap-3 ${isDark ? "border-[#3A3A3A]" : "border-gray-200"
            }`}
        >
          <button
            onClick={() => setShowConfirm(true)}
            className="min-w-[130px] h-9 rounded-md text-[11px] font-bold text-white shadow-lg transition-transform active:scale-95"
            style={{
              background: "linear-gradient(180deg, #A82020 0%, #5E0A0A 100%)",
              border: "1px solid #A82020",
              boxShadow:
                "0px 2px 4px rgba(0, 0, 0, 0.25), inset 0px 1px 1px rgba(255, 255, 255, 0.1)",
            }}
          >
            Delete
          </button>

          <button
            onClick={onClose}
            className="min-w-[130px] h-9 rounded-md text-[11px] font-bold text-[#1F1F1F] shadow-lg transition-transform active:scale-95"
            style={{
              background: "linear-gradient(180deg, #C6D92C 0%, #8E9E1A 100%)",
              border: "1px solid #C6D92C",
              boxShadow:
                "0px 2px 4px rgba(0, 0, 0, 0.25), inset 0px 1px 1px rgba(255, 255, 255, 0.2)",
            }}
          >
            Maybe later
          </button>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => {
          onDelete();
          setShowConfirm(false);
        }}
        title="Delete"
        message="Do you really want to delete this license?"
        confirmText="Confirm"
        cancelText="Maybe later"
        type="delete"
      />
    </div>
  );
};

export default DeleteLicenseModal;
